<script setup lang="ts">
import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { BarChartSpec } from "../sbi/protocol";
import { baseOption } from "./echarts-base";
import { autoYDomainFor } from "./chart-yscale.ts";
import {
  categoryLabelPolicy,
  plotWidthEstimate,
  verticalCategoryLabel,
} from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

const props = withDefaults(
  defineProps<{
    spec: BarChartSpec;
    /** Field → format registry; `spec.y` is the fallback lookup key. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

// `spec.data` is a `ChartData` union (rows-array | SqlMarker |
// DeriveMarker). The InsightChart dispatcher only mounts this
// subcomponent after the marker has resolved — narrow with
// `Array.isArray` so the rest of the component can index/map without
// repeated unions, and so a defective dispatch path can't crash render.
const rows = computed(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const option = computed(() => {
  const base = baseOption(
    {
      title: props.spec.title,
      // Per-axis `yFormat` → per-call `format` (DefaultOpts) → registry
      // format for the y field.
      yFormat: props.spec.yFormat ?? props.spec.format ?? props.formats[props.spec.y],
    },
    ctx.value,
  );
  const categoryAxis = {
    ...base.xAxis,
    type: "category",
    data: rows.value.map((row) => row[props.spec.x]),
  };
  // Auto-scale, when engaged, computes a tight domain over the y-field
  // values; result is spread onto the value axis regardless of
  // orientation so horizontal bars get the same tight domain on their
  // (now x-side) numeric axis.
  const autoDomain = autoYDomainFor(
    props.spec.yScale,
    rows.value.map((row) => row[props.spec.y]),
  );
  const valueAxis = {
    ...base.yAxis,
    type: "value" as const,
    ...(autoDomain ?? {}),
  };

  // Horizontal mode puts category labels on the y-axis. `containLabel`
  // (base grid) sizes the gutter to them; the width cap — 30% of the
  // chart, 48–160px — stops one long name squeezing the bars to
  // nothing, and truncates past it (full name in the tooltip).
  // Vertical mode rotates and truncates labels that don't fit their
  // slot instead of letting ECharts silently drop them.
  const xLabels = categoryAxis.data;
  const horizontalCategoryAxis = {
    ...categoryAxis,
    axisLabel: {
      ...(categoryAxis as { axisLabel?: object }).axisLabel,
      ...verticalCategoryLabel(xLabels, ctx.value.width),
    },
  };
  const verticalCategoryAxis = {
    ...categoryAxis,
    axisLabel: {
      ...(categoryAxis as { axisLabel?: object }).axisLabel,
      ...categoryLabelPolicy({
        labels: xLabels,
        plotWidth: plotWidthEstimate(ctx.value.width),
        mode: "rotate",
      }),
    },
  };

  return {
    ...base,
    xAxis: props.spec.horizontal ? valueAxis : verticalCategoryAxis,
    yAxis: props.spec.horizontal ? horizontalCategoryAxis : valueAxis,
    series: [
      {
        type: "bar",
        data: rows.value.map((row) => row[props.spec.y]),
      },
    ],
    legend: { show: false },
  };
});

const height = computed(() =>
  resolveChartHeight("bar", props.spec.height),
);

function onClick(p: { dataIndex: number }) {
  if (props.spec.id && p && typeof p.dataIndex === "number") {
    emit(
      "click-target",
      props.spec.id,
      rows.value[p.dataIndex] as Record<string, unknown>,
    );
  }
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
